/**
 * FleetBoard — the shared rich race-board renderer for edge bots.
 *
 * Lifted verbatim out of the Recycler page so BOTH the Bots page and the
 * Position Tracker render the identical rows: the queue fill-anchor, the heat
 * ramp, the full metric band (front / resting / climb / fill-ETA / velocity /
 * markout / net), the climb sparkline, the claim/own attribution cell, the
 * per-agent controls, and the expandable queue-ladder detail. Three view modes
 * (compact / card / table) are a layout preference over the same data.
 *
 * The host owns the data: it passes an ALREADY filtered + sorted `agents` list,
 * the per-agent `motion` map, the open-row id, and a normalized id-based
 * `FleetControls` bundle. The wrapper adapts those controls into the row
 * components' existing prop shape, so the leaf renderers stay unchanged. `reband`
 * is optional — a host without a reband endpoint (the tracker) simply omits it
 * and the Reband affordance is hidden.
 */
import { Pause, Play, Trash2, SlidersHorizontal, User } from 'lucide-react';
import { Pill } from '@/components/cards/cardKit';
import { TickerDisplay } from '@/components/atoms/TickerDisplay';
import { Sparkline } from '@/components/atoms';
import type { AgentStats } from '@/lib/bots/edge';
import {
  heat,
  postureTone,
  frontOf,
  hasLiveQueue,
  restingTotal,
  progressPct,
  legsOf,
  uptime,
  fmtClimb,
  fmtEta,
  fmtVel,
  fmtNet,
  markout5s,
  queueState,
  type Motion,
  type ViewMode,
} from '@/lib/bots/telemetry';

/** The normalized, id-keyed control bundle both hosts supply. `kill` is a PLAIN
 *  kill request — the confirm dialog lives in the host (each wires kill → its own
 *  ConfirmModal). `reband` is optional; omit it where there's no edge endpoint. */
export interface FleetControls {
  pause: (id: string) => void;
  resume: (id: string) => void;
  kill: (id: string) => void;
  claim: (id: string, ticker: string) => void;
  release: (id: string) => void;
  reband?: (id: string) => void;
}

export interface FleetBoardProps {
  /** already filtered + sorted by the host — the board is source-agnostic. */
  agents: AgentStats[];
  motion: Map<string, Motion>;
  view: ViewMode;
  openId: string | null;
  onOpen: (id: string) => void;
  meUsername?: string;
  controls: FleetControls;
  /** per-row gate for the mutating controls (pause/stop/reband). Defaults to
   *  always-shown (the operator-facing Bots page); the tracker passes
   *  ownership/role so a trader sees pause/stop only on bots it can actually act
   *  on — matching the relay's owner-only authz, so no control ever silently 403s. */
  canMutate?: (a: AgentStats) => boolean;
  /** shown when `agents` is empty (e.g. a filter with no matches). */
  emptyHint?: string;
}

export function FleetBoard({ agents, motion, view, openId, onOpen, meUsername, controls, canMutate, emptyHint }: FleetBoardProps) {
  const open = agents.find((a) => a.id === openId) ?? null;

  // Adapt the normalized id-based controls into the leaf renderers' existing
  // RowProps shape (kept unchanged to minimize diff + regression surface).
  const rowProps = (a: AgentStats): RowProps => ({
    a,
    motion: motion.get(a.id) ?? { climb: 0, eta: -1, spark: [] },
    open: openId === a.id,
    onOpen: () => onOpen(a.id),
    onAct: actAdapter,
    onReband: controls.reband ? () => controls.reband!(a.id) : undefined,
    onKill: () => controls.kill(a.id),
    meUsername,
    onClaim: (x) => controls.claim(x.id, x.ticker),
    onRelease: (x) => controls.release(x.id),
    // Explicit predicate wins (the tracker passes ownership/role); otherwise
    // fall back to the relay's server-computed canOperate (so the operator Bots
    // page is org-guarded too), defaulting permissive only if it's absent.
    canMutate: canMutate ? canMutate(a) : (a.canOperate ?? true),
  });

  if (agents.length === 0) {
    return emptyHint ? (
      <div className="rounded-md border border-dashed border-border px-3 py-4 text-center text-xs text-muted-foreground">
        {emptyHint}
      </div>
    ) : null;
  }

  return (
    <>
      {view === 'card' ? (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(300px,1fr))] gap-3">
          {agents.map((a) => (
            <RecyclerCard key={a.id} {...rowProps(a)} />
          ))}
        </div>
      ) : view === 'table' ? (
        <FleetTable
          rows={agents}
          motion={motion}
          onOpen={onOpen}
          onAct={actAdapter}
          onKill={(id) => controls.kill(id)}
          meUsername={meUsername}
          onClaim={(x) => controls.claim(x.id, x.ticker)}
          onRelease={(x) => controls.release(x.id)}
          canMutate={canMutate}
        />
      ) : (
        <div className="space-y-2.5">
          {agents.map((a) => (
            <RecyclerRow key={a.id} {...rowProps(a)} />
          ))}
        </div>
      )}

      {open && (
        <AgentDetail
          agent={open}
          motion={motion.get(open.id) ?? { climb: 0, eta: -1, spark: [] }}
          onReband={controls.reband ? () => controls.reband!(open.id) : undefined}
        />
      )}
    </>
  );

  function actAdapter(id: string, action: 'pause' | 'resume' | 'kill') {
    if (action === 'pause') controls.pause(id);
    else if (action === 'resume') controls.resume(id);
    else controls.kill(id);
  }
}

// ── the colored progress anchor (sits behind the content). ──────────────────
// One fill treatment for ALL views (compact/card/table use the identical gradient):
// a tinted band up to the queue position, capped by a 2px leading-edge line, so the
// fill reads the same everywhere — the view is a layout preference, not different data.
function fillGradient(pct: number, rgb: string, vertical?: boolean): string {
  const edge = pct >= 100 ? 'calc(100% - 2px)' : `${pct}%`;
  const dir = vertical ? 'to top' : 'to right';
  return `linear-gradient(${dir}, rgb(${rgb} / 0.16) 0%, rgb(${rgb} / 0.16) ${edge}, rgb(${rgb} / 0.9) ${edge}, rgb(${rgb} / 0.9) calc(${edge} + 2px), transparent calc(${edge} + 2px))`;
}
function FillBg({ pct, rgb, vertical }: { pct: number; rgb: string; vertical?: boolean }) {
  return (
    <div
      className="pointer-events-none absolute inset-0"
      style={{ backgroundImage: fillGradient(pct, rgb, vertical) }}
      aria-hidden
    />
  );
}

interface RowProps {
  a: AgentStats;
  motion: Motion;
  open: boolean;
  onOpen: () => void;
  onAct: (id: string, action: 'pause' | 'resume' | 'kill') => void;
  onReband?: () => void;
  onKill: () => void;
  meUsername?: string;
  onClaim: (a: AgentStats) => void;
  onRelease: (a: AgentStats) => void;
  canMutate: boolean;
}

// Compact row layout, left → right:
//   [identity: flexes to fill]  [metric band: FIXED columns]  [actions: FIXED width]
// Because both the band and the actions zone are fixed-width and the identity zone
// absorbs all the slack, the band's left edge lands at the SAME x on every row — so
// the metric columns line up vertically and stay comparable across tickers.
const COMPACT_BAND_COLS = '52px 68px 60px 78px 84px 70px 82px 50px 56px';

function RecyclerRow({ a, motion, onOpen, onAct, onReband, onKill, meUsername, onClaim, onRelease, canMutate }: RowProps) {
  const front = frontOf(a);
  const h = heat(front);
  const q = hasLiveQueue(a);
  const qs = queueState(a);
  const pt = postureTone(a.posture);
  return (
    <div
      onClick={onOpen}
      className={`relative cursor-pointer overflow-hidden rounded-lg border border-border bg-card transition-colors hover:border-border/80 hover:bg-foreground/[0.03] ${a.paused ? 'opacity-60' : ''}`}
    >
      <FillBg pct={progressPct(front)} rgb={h.rgb} />
      <div className="relative z-10 flex items-center gap-x-4 px-4 py-2.5">
        <div className="flex min-w-0 flex-1 items-center gap-2.5">
          <span
            title={`bot health: ${pt.label}`}
            className={`h-2 w-2 shrink-0 rounded-full ${a.paused ? 'bg-muted-foreground' : pt.dot}`}
          />
          <span className="min-w-0 truncate">
            <TickerDisplay ticker={a.ticker} variant="compact" showRaw={false} />
          </span>
          <span className="hidden shrink-0 xl:block">
            <Intent a={a} />
          </span>
          <span className="shrink-0">
            <ModeBadge a={a} />
          </span>
        </div>
        <div className="grid flex-none items-center gap-x-3" style={{ gridTemplateColumns: COMPACT_BAND_COLS }}>
          <Stat right label="front" value={q ? (front < 0 ? '—' : String(front)) : '—'} cls={q ? h.text : 'text-muted-foreground/40'} />
          <Stat right label="queue" value={qs.label} cls={qs.cls} />
          <Stat right label="resting" value={String(restingTotal(a))} />
          <Stat right label="climb" value={q ? fmtClimb(motion.climb) : '—'} est={q} cls={q ? undefined : 'text-muted-foreground/40'} />
          <Stat right label="fill eta" value={q ? fmtEta(motion.eta) : '—'} est={q} cls={q ? undefined : 'text-muted-foreground/40'} />
          <Stat right label="velocity" value={fmtVel(a.lineVelocityCentsPerSec)} />
          <Stat right label="markout 5s" value={markout5s(a)} />
          <Stat right label="net" value={fmtNet(a.netInventory)} cls={a.netInventory !== 0 ? 'text-amber-400' : undefined} />
          <div className="flex h-5 items-center justify-end">
            {motion.spark.length >= 2 ? (
              <Sparkline data={motion.spark} width={50} height={20} color="auto" />
            ) : (
              <span className="text-muted-foreground/30">—</span>
            )}
          </div>
        </div>
        <div className="flex w-[150px] flex-none items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
          <ClaimCell a={a} meUsername={meUsername} onClaim={onClaim} onRelease={onRelease} />
          <Controls a={a} canMutate={canMutate} onAct={onAct} onReband={onReband} onKill={onKill} />
        </div>
      </div>
    </div>
  );
}

function RecyclerCard({ a, motion, onOpen, onAct, onReband, onKill, meUsername, onClaim, onRelease, canMutate }: RowProps) {
  const front = frontOf(a);
  const h = heat(front);
  const q = hasLiveQueue(a);
  const qs = queueState(a);
  const pt = postureTone(a.posture);
  return (
    <div
      onClick={onOpen}
      className={`relative flex min-h-[156px] cursor-pointer flex-col overflow-hidden rounded-lg border border-border bg-card transition-colors hover:border-border/80 hover:bg-foreground/[0.03] ${a.paused ? 'opacity-60' : ''}`}
    >
      <FillBg pct={progressPct(front)} rgb={h.rgb} vertical />
      <div className="relative z-10 flex flex-1 flex-col gap-2 p-3.5">
        <div className="flex items-start justify-between gap-2">
          <span className="flex min-w-0 items-center gap-2">
            <span
              title={`bot health: ${pt.label}`}
              className={`h-2 w-2 shrink-0 rounded-full ${a.paused ? 'bg-muted-foreground' : pt.dot}`}
            />
            <span className="min-w-0 truncate">
              <TickerDisplay ticker={a.ticker} variant="compact" showRaw={false} />
            </span>
          </span>
          <Pill className={`${pt.cls} bg-foreground/5`} title={`bot health: ${a.posture}`}>
            {pt.label}
          </Pill>
        </div>
        <Intent a={a} />
        <div className="grid grid-cols-3 gap-x-3 gap-y-1.5">
          <Stat label="front" value={q ? (front < 0 ? '—' : String(front)) : '—'} cls={q ? h.text : 'text-muted-foreground/40'} />
          <Stat label="queue" value={qs.label} cls={qs.cls} />
          <Stat label="fill eta" value={q ? fmtEta(motion.eta) : '—'} est={q} cls={q ? undefined : 'text-muted-foreground/40'} />
          <Stat label="climb" value={q ? fmtClimb(motion.climb) : '—'} est={q} cls={q ? undefined : 'text-muted-foreground/40'} />
          <Stat label="resting" value={String(restingTotal(a))} />
          <Stat label="velocity" value={fmtVel(a.lineVelocityCentsPerSec)} />
          <Stat label="net" value={fmtNet(a.netInventory)} cls={a.netInventory !== 0 ? 'text-amber-400' : undefined} />
        </div>
        <div className="mt-auto flex items-center justify-between gap-2 pt-1">
          <ModeBadge a={a} />
          <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
            <ClaimCell a={a} meUsername={meUsername} onClaim={onClaim} onRelease={onRelease} />
            <Controls a={a} canMutate={canMutate} onAct={onAct} onReband={onReband} onKill={onKill} />
          </div>
        </div>
      </div>
    </div>
  );
}

function FleetTable({
  rows,
  motion,
  onOpen,
  onAct,
  onKill,
  meUsername,
  onClaim,
  onRelease,
  canMutate,
}: {
  rows: AgentStats[];
  motion: Map<string, Motion>;
  onOpen: (id: string) => void;
  onAct: (id: string, action: 'pause' | 'resume' | 'kill') => void;
  onKill: (id: string) => void;
  meUsername?: string;
  onClaim: (a: AgentStats) => void;
  onRelease: (a: AgentStats) => void;
  canMutate?: (a: AgentStats) => boolean;
}) {
  // Same default as the card/compact path: no explicit predicate → fall back to
  // the relay's server-computed canOperate so the table view is org-guarded too.
  const cm = canMutate ?? ((a: AgentStats) => a.canOperate ?? true);
  return (
    <div className="overflow-hidden rounded-lg border border-border bg-card">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[11px] uppercase tracking-wide text-muted-foreground">
            <th className="px-4 py-2 font-normal">market</th>
            <th className="px-2 py-2 font-normal">intent</th>
            <th className="px-2 py-2 text-right font-normal">front</th>
            <th className="px-2 py-2 text-right font-normal">queue</th>
            <th className="px-2 py-2 text-right font-normal">fill eta</th>
            <th className="px-2 py-2 text-right font-normal">resting</th>
            <th className="px-2 py-2 text-right font-normal">net</th>
            <th className="px-2 py-2 text-right font-normal">err</th>
            <th className="px-4 py-2 text-right font-normal">actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((a) => {
            const front = frontOf(a);
            const h = heat(front);
            const pct = progressPct(front);
            const pt = postureTone(a.posture);
            const q = hasLiveQueue(a);
            const qs = queueState(a);
            const m = motion.get(a.id) ?? { climb: 0, eta: -1, spark: [] };
            // Full-row fill: a tinted band spanning the WHOLE row up to the queue
            // position, capped by a 2px leading edge line so 100% (= at the front)
            // is unmistakable. backgroundImage layers over hover's background-color.
            // No row fill until a live queue position is present (q === front>=0).
            const rowFill = q ? fillGradient(pct, h.rgb) : undefined;
            return (
              <tr
                key={a.id}
                onClick={() => onOpen(a.id)}
                style={{ backgroundImage: rowFill }}
                className={`cursor-pointer border-t border-border hover:bg-foreground/5 ${a.paused ? 'opacity-60' : ''}`}
              >
                <td className="px-4 py-2.5">
                  <span className="flex items-center gap-2">
                    <span className={`h-2 w-2 shrink-0 rounded-full ${a.paused ? 'bg-muted-foreground' : pt.dot}`} />
                    <TickerDisplay ticker={a.ticker} variant="compact" showRaw={false} />
                  </span>
                </td>
                <td className="px-2 py-2.5">
                  <Intent a={a} />
                </td>
                <td className={`px-2 py-2.5 text-right font-mono ${q ? h.text : 'text-muted-foreground/40'}`}>{q ? (front < 0 ? '—' : front) : '—'}</td>
                <td className={`px-2 py-2.5 text-right font-mono ${qs.cls}`}>{qs.label}</td>
                <td className={`px-2 py-2.5 text-right font-mono ${q ? 'text-muted-foreground' : 'text-muted-foreground/40'}`}>{q ? fmtEta(m.eta) : '—'}</td>
                <td className="px-2 py-2.5 text-right font-mono">{restingTotal(a)}</td>
                <td className="px-2 py-2.5 text-right font-mono">
                  <span className={a.netInventory !== 0 ? 'text-amber-400' : ''}>{fmtNet(a.netInventory)}</span>
                </td>
                <td className="px-2 py-2.5 text-right font-mono">
                  <span className={a.errors > 0 ? 'text-red-400' : 'text-muted-foreground'}>{a.errors}</span>
                </td>
                <td className="px-4 py-2.5 text-right" onClick={(e) => e.stopPropagation()}>
                  <div className="inline-flex items-center gap-2 text-muted-foreground">
                    <ClaimCell a={a} meUsername={meUsername} onClaim={onClaim} onRelease={onRelease} />
                    {cm(a) && (
                      <>
                        {a.paused ? (
                          <button title="resume" onClick={() => onAct(a.id, 'resume')} className="hover:text-green-400">
                            <Play size={15} />
                          </button>
                        ) : (
                          <button title="pause" onClick={() => onAct(a.id, 'pause')} className="hover:text-foreground">
                            <Pause size={15} />
                          </button>
                        )}
                        <button title="kill" onClick={() => onKill(a.id)} className="text-red-400/70 hover:text-red-400">
                          <Trash2 size={15} />
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function AgentDetail({ agent, motion, onReband }: { agent: AgentStats; motion: Motion; onReband?: () => void }) {
  const resting = agent.resting ?? [];
  const bids = resting.filter((o) => o.side === 'bid').sort((a, b) => a.queuePosition - b.queuePosition);
  const asks = resting.filter((o) => o.side === 'ask').sort((a, b) => a.queuePosition - b.queuePosition);
  const pt = postureTone(agent.posture);
  const front = frontOf(agent);
  const q = front >= 0;
  const qs = queueState(agent);
  return (
    <div className="mt-4 rounded-lg border border-border bg-card p-4">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <TickerDisplay ticker={agent.ticker} variant="full" showRaw />
        <Pill className={`${pt.cls} bg-foreground/5`}>{pt.label}</Pill>
        <span className="text-xs text-muted-foreground">
          {agent.kind} · {agent.mode} · up {uptime(agent.startedAtMs)}
        </span>
        {onReband && (
          <button onClick={onReband} className="ml-auto inline-flex items-center gap-1 rounded border border-border px-2.5 py-1 text-xs hover:bg-foreground/5">
            <SlidersHorizontal size={12} /> Reband (live retune)
          </button>
        )}
      </div>

      <div className="mb-4 grid grid-cols-3 gap-2 sm:grid-cols-6">
        <Mini label="front" value={front < 0 ? '—' : String(front)} cls={q ? heat(front).text : 'text-muted-foreground/40'} />
        <Mini label="queue" value={qs.label} cls={qs.cls} />
        <Mini label={q ? 'fill eta ~est' : 'fill eta'} value={q ? fmtEta(motion.eta) : '—'} cls={q ? undefined : 'text-muted-foreground/40'} />
        <Mini label={q ? 'climb ~est' : 'climb'} value={q ? fmtClimb(motion.climb) : '—'} cls={q ? undefined : 'text-muted-foreground/40'} />
        <Mini label="velocity" value={fmtVel(agent.lineVelocityCentsPerSec)} />
        <Mini label="markout 5s" value={markout5s(agent)} />
        <Mini label="net inv" value={fmtNet(agent.netInventory)} cls={agent.netInventory !== 0 ? 'text-amber-400' : undefined} />
        <Mini label="resting" value={String(restingTotal(agent))} />
        <Mini label="placed" value={String(agent.placed)} />
        <Mini label="canceled" value={String(agent.canceled)} />
        <Mini label="loops" value={agent.loops.toLocaleString()} />
        <Mini label="errors" value={String(agent.errors)} cls={agent.errors > 0 ? 'text-red-400' : undefined} />
        <Mini label="fair" value={agent.lastFairCents?.toFixed(1) ?? '—'} />
      </div>

      {resting.length > 0 ? (
        <>
          <div className="mb-2 text-xs text-muted-foreground">
            queue ladder — contracts ahead of each chunk (front → back).{' '}
            <span className="text-green-400">at the front</span> · <span className="text-amber-400">near</span> ·{' '}
            <span className="text-red-400">deep</span>
          </div>
          {([
            { label: 'buy', chunks: bids },
            { label: 'sell', chunks: asks },
          ] as const).map((leg) =>
            leg.chunks.length === 0 ? null : (
              <div key={leg.label} className="mb-2 flex items-center gap-2">
                <div className="w-20 text-sm">
                  <span className="text-muted-foreground">{leg.label}</span>{' '}
                  <span className="font-mono">{leg.chunks[0].priceCents}¢</span>
                </div>
                <div className="flex flex-1 gap-1">
                  {leg.chunks.map((c) => {
                    const ch = heat(c.queuePosition);
                    return (
                      <div
                        key={c.id}
                        className={`group relative flex-1 rounded px-2 py-2 text-center font-mono text-xs ${ch.fill} ${ch.text}`}
                        title={`${c.sizeContracts} contracts · ${c.queuePosition < 0 ? 'queue unknown' : c.queuePosition + ' ahead'} · ${c.queueSource || 'no source'}`}
                      >
                        {c.queuePosition < 0 ? '?' : c.queuePosition}
                        <div className="pointer-events-none absolute inset-x-0 -bottom-5 hidden justify-center gap-1 group-hover:flex">
                          <span className="rounded bg-sky-500/20 px-1 text-[10px] text-sky-400">let ride</span>
                          <span className="rounded bg-red-500/20 px-1 text-[10px] text-red-400">cut</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ),
          )}
          <div className="mt-6 text-[11px] leading-relaxed text-muted-foreground">
            <span className="text-foreground">let ride / cut</span> = the per-order convert layer (a trader keeps a chunk to a
            fill, or cuts it). Pending a per-order edge endpoint — the API is agent-level today.
          </div>
        </>
      ) : (
        <div className="text-sm text-muted-foreground">No resting orders right now (the bot is between recycle cycles).</div>
      )}
    </div>
  );
}

// ── small shared pieces ─────────────────────────────────────────────────────
function Intent({ a }: { a: AgentStats }) {
  const ls = legsOf(a);
  if (!ls.length) return <span className="text-xs text-muted-foreground">idle · recycling</span>;
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-sm">
      {ls.map((l) => (
        <span key={l.side} className="whitespace-nowrap">
          <span className="text-muted-foreground">{l.side === 'bid' ? 'buy' : 'sell'}</span>{' '}
          <span className="font-medium">{l.size}</span> <span className="text-muted-foreground">@ {l.price}¢</span>
        </span>
      ))}
    </div>
  );
}
function Stat({ label, value, cls, est, right }: { label: string; value: string; cls?: string; est?: boolean; right?: boolean }) {
  return (
    <div className={`min-w-0 leading-tight ${right ? 'text-right' : ''}`}>
      <div className="truncate text-[10px] uppercase tracking-wide text-muted-foreground">
        {label}
        {est && <span className="ml-0.5 lowercase opacity-60">~est</span>}
      </div>
      <div className={`truncate font-mono text-sm ${cls ?? 'text-foreground'}`}>{value}</div>
    </div>
  );
}
function Mini({ label, value, cls }: { label: string; value: string; cls?: string }) {
  return (
    <div className="rounded border border-border bg-background/40 p-2 text-center">
      <div className="text-[10px] text-muted-foreground">{label}</div>
      <div className={`mt-0.5 font-mono text-sm ${cls ?? ''}`}>{value}</div>
    </div>
  );
}
function ModeBadge({ a }: { a: AgentStats }) {
  if (a.haltReason) {
    return (
      <Pill className="bg-red-500/15 text-red-400" title="halted after a fill; resting quotes were pulled">
        halted
      </Pill>
    );
  }
  return a.mode === 'cross_for_fills' ? (
    <Pill className="bg-sky-500/15 text-sky-400" title="filling — crosses to take fills">
      let it ride
    </Pill>
  ) : (
    <Pill className="bg-foreground/5 text-muted-foreground" title="auto-recycle — post-only, rotates back before filling">
      auto-recycle
    </Pill>
  );
}
function ClaimCell({
  a,
  meUsername,
  onClaim,
  onRelease,
}: {
  a: AgentStats;
  meUsername?: string;
  onClaim: (a: AgentStats) => void;
  onRelease: (a: AgentStats) => void;
}) {
  const owner = a.owner;
  if (!owner) {
    // Claiming needs org membership (relay-computed canClaim). An admin viewing
    // another org sees the bot but can't stake it — show an inert marker, not a
    // claim button that would 403.
    if (a.canClaim === false) {
      return (
        <span
          title="join this bot's org to claim it"
          className="rounded border border-border/60 px-2 py-0.5 text-[11px] text-muted-foreground/60"
        >
          view only
        </span>
      );
    }
    return (
      <button
        onClick={() => onClaim(a)}
        title="claim this bot under your name"
        className="rounded border border-border px-2 py-0.5 text-[11px] text-muted-foreground hover:border-primary hover:text-foreground"
      >
        claim
      </button>
    );
  }
  if (owner.username === meUsername) {
    return (
      <button
        onClick={() => onRelease(a)}
        title="release back to the pool"
        className="rounded border border-primary/40 bg-primary/10 px-2 py-0.5 text-[11px] text-primary hover:bg-primary/20"
      >
        release
      </button>
    );
  }
  return (
    <span
      title={`claimed by ${owner.username}`}
      className="inline-flex max-w-[96px] items-center gap-1 rounded border border-border px-2 py-0.5 text-[11px] text-muted-foreground"
    >
      <User size={11} className="shrink-0" /> <span className="truncate">{owner.username}</span>
    </span>
  );
}
function Controls({
  a,
  canMutate,
  onAct,
  onReband,
  onKill,
}: {
  a: AgentStats;
  canMutate: boolean;
  onAct: (id: string, action: 'pause' | 'resume' | 'kill') => void;
  onReband?: () => void;
  onKill: () => void;
}) {
  if (!canMutate) return null;
  return (
    <div className="flex items-center gap-2 text-muted-foreground">
      {a.paused ? (
        <button title="resume" onClick={() => onAct(a.id, 'resume')} className="hover:text-green-400">
          <Play size={15} />
        </button>
      ) : (
        <button title="pause" onClick={() => onAct(a.id, 'pause')} className="hover:text-foreground">
          <Pause size={15} />
        </button>
      )}
      {onReband && (
        <button title="reband (live retune)" onClick={onReband} className="hover:text-foreground">
          <SlidersHorizontal size={15} />
        </button>
      )}
      <button title="kill" onClick={onKill} className="text-red-400/70 hover:text-red-400">
        <Trash2 size={15} />
      </button>
    </div>
  );
}
